import { useCallback, useState } from 'react';
import { VoiceTab } from '@avatar/contexts/tabs';
import { useCreateProject } from '../../../Projects/graphql.mutations';
import {
  CreateProjectInputs,
  StudioProjectVersion,
} from '../../../../../server/graphql/__generated.types';
import parseApolloError from '../../../../util/apolloError';
import useProjectVersion, {
  AZURE_VERSION_PLACEHOLDER,
  useProjectVersionProps,
} from './useProjectVersion';
import { DriveType } from '../../../Drives/types';
import { ProjectCreationSource } from '../../../../services/eventTrackingServiceTypes';
import { toast } from '../../../Toaster';

export interface useProjectCreateFormProps extends useProjectVersionProps {
  folderId?: string;
  driveId?: string;
  onSuccess: (projectId: string, projectVersion?: StudioProjectVersion) => void;
  driveType?: DriveType;
  source: ProjectCreationSource;
  tab?: VoiceTab;
  variantId?: string;
}

const DEFAULT_PROJECT_NAME = 'New project';
export const OTHER_LANGUAGES_EMPTY_ERROR = 'OTHER_LANGUAGES_EMPTY_ERROR';
export const PROJECT_NAME_EMPTY_ERROR = 'PROJECT_NAME_EMPTY_ERROR';
const UNEXPECTED_SERVER_ERROR = 'Something went wrong. Please try again';

/**
 * State management for create project form
 * Implements gql call for form submission
 * - Creates @var modelOptions to assist with available options to render in UI
 */
const useProjectCreateForm = (props: useProjectCreateFormProps) => {
  const {
    userVersions: incomingUserVersions,
    enabledVersions: incomingEnabledVersions,
    isAdditionalLanguagesUpgrade,
    onSuccess,
    folderId,
    driveId,
    driveType,
    source,
    tab,
    variantId,
  } = props;

  const [createProject] = useCreateProject({
    driveType,
    source,
    tab,
    variantId,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [userVersions, setUserVersions] = useState(incomingUserVersions);
  const [enabledVersions, setEnabledVersions] = useState(
    incomingEnabledVersions
  );

  const { getProjectVersionOptions } = useProjectVersion();

  const [modelOptions, setModelOptions] = useState(
    getProjectVersionOptions({
      userVersions,
      enabledVersions,
      isAdditionalLanguagesUpgrade,
    })
  );

  const [currentModel, setActiveModel] = useState(0);

  const [inputs, setInputs] = useState<CreateProjectInputs>({
    name: DEFAULT_PROJECT_NAME,
    clientName: '',
    studioVersion: StudioProjectVersion.Universal,
  });

  const handleInputChange = (updatedInputs: CreateProjectInputs) => {
    setError(undefined);
    setInputs(updatedInputs);
  };

  const handleModelChange = useCallback(
    (index: number, studioVersion: StudioProjectVersion) => {
      setActiveModel(index);
      setInputs(prev => ({ ...prev, studioVersion }));
    },
    []
  );

  const reduceErrorMessage = (message: string) => {
    const isInvalidCharacters = message.includes('invalid characters');
    return isInvalidCharacters
      ? `Failed to create project: ${message}`
      : UNEXPECTED_SERVER_ERROR;
  };

  const submit = useCallback(async () => {
    try {
      setLoading(true);

      const studioVersion = inputs.studioVersion as string;
      if (studioVersion === AZURE_VERSION_PLACEHOLDER) {
        setError(OTHER_LANGUAGES_EMPTY_ERROR);
        return;
      }

      if (!inputs.name || inputs.name.length === 0) {
        setError(PROJECT_NAME_EMPTY_ERROR);
        return;
      }

      const inputsWithLocationInformation = { ...inputs, folderId, driveId };

      const result = await createProject({
        variables: { inputs: inputsWithLocationInformation },
      });

      if (
        result.data?.createProject?.success &&
        result.data.createProject?.project
      ) {
        onSuccess(
          result.data.createProject.project.id,
          inputs.studioVersion || undefined
        );
        return;
      }

      if (result.data?.createProject && !result.data.createProject.success) {
        setError(result.data.createProject.message);
        const message = reduceErrorMessage(result.data.createProject.message);
        toast({ message, type: 'error' });
      }
    } catch (_err) {
      if (_err instanceof Error) {
        const apolloErr = parseApolloError(_err);
        setError(apolloErr);
        const message = reduceErrorMessage(apolloErr);
        toast({ message, type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [inputs, folderId, driveId, createProject, onSuccess]);

  const reset = (options: useProjectVersionProps) => {
    setUserVersions(options.userVersions);
    setEnabledVersions(options.enabledVersions);

    const updatedOptions = getProjectVersionOptions({
      userVersions: options.userVersions,
      enabledVersions: options.enabledVersions,
      isAdditionalLanguagesUpgrade,
    });

    setModelOptions(updatedOptions);
    setInputs({
      name: DEFAULT_PROJECT_NAME,
      clientName: '',
      studioVersion: StudioProjectVersion.Universal,
      driveId: null,
      folderId: null,
    });
    setActiveModel(0);
    setLoading(false);
    setError(undefined);
  };

  return {
    currentModel,
    error,
    inputs,
    loading,
    modelOptions,
    onChange: handleInputChange,
    onModelChange: handleModelChange,
    submit,
    reset,
  };
};

export default useProjectCreateForm;
